/**
 * Set document.title + meta description + meta keywords + Open-Graph tags +
 * canonical URL based on data coming from the CMS.
 *
 * Used by PublicSalon (per-page SEO) and other public surfaces. Designed to be
 * a single source of truth — every public page imports this so we never miss
 * a meta tag again.
 */
import { useEffect } from "react";

function setMeta(name, content) {
  if (!content) return removeMeta(name);
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setProperty(prop, content) {
  if (!content) return removeProperty(prop);
  let el = document.head.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(name) {
  const el = document.head.querySelector(`meta[name="${name}"]`);
  if (el) el.remove();
}
function removeProperty(prop) {
  const el = document.head.querySelector(`meta[property="${prop}"]`);
  if (el) el.remove();
}

function setCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!url) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

/**
 * Apply SEO to <head>.
 *
 * @param {object} opts
 * @param {string} opts.title          page <title>
 * @param {string} opts.description    meta[name=description]
 * @param {string} [opts.keywords]     meta[name=keywords]
 * @param {string} [opts.ogImage]      og:image
 * @param {string} [opts.ogType]       og:type (default "website")
 * @param {string} [opts.canonical]    rel=canonical (default = current URL)
 */
export function useDocumentSeo({ title, description, keywords, ogImage, ogType = "website", canonical } = {}) {
  useEffect(() => {
    // No-op when the caller has nothing meaningful to set. This lets a parent
    // component skip SEO for special routes (booking, blog post) and delegate
    // to a child without the parent's effect overwriting the child's tags
    // afterwards (effects fire child-first, parent-second).
    if (!title) return;
    document.title = title;
    setMeta("description", description);
    setMeta("keywords", keywords);
    setProperty("og:title", title);
    setProperty("og:description", description);
    setProperty("og:type", ogType);
    setProperty("og:image", ogImage);
    setProperty("og:url", canonical || window.location.href);
    setMeta("twitter:card", ogImage ? "summary_large_image" : "summary");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
    setCanonical(canonical || window.location.href);
  }, [title, description, keywords, ogImage, ogType, canonical]);
}

/**
 * Convenience: build SEO props from a CMS page document + salon header.
 * Falls back gracefully when fields are missing.
 */
export function seoFromPage({ page, salon, header }) {
  const seo = (page && page.seo) || {};
  const salonName = (header && header.brand_name) || (salon && salon.name) || "";
  // Title: explicit SEO title wins. Otherwise compose "{page title} | {salon}".
  const title = seo.title
    || (page && page.title && salonName
      ? `${page.title} | ${salonName}`
      : (page && page.title) || salonName);
  return {
    title,
    description: seo.description || "",
    keywords: seo.keywords || "",
    ogImage: seo.og_image || (header && header.logo_url) || null,
  };
}
